<template>
  <nav v-if="userProfile" class="sticky top-0 z-30 transition-all duration-300"
       :class="scrolled ? 'bg-white/80 dark:bg-surface-950/80 backdrop-blur-xl shadow-elevation-1' : 'bg-white/50 dark:bg-surface-950/50 backdrop-blur-md'">
    <!-- Accent line -->
    <div class="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent"></div>

    <div class="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Left: Logo + Nav -->
        <div class="flex items-center">
          <!-- Logo -->
          <router-link
            :to="userProfile.account_type === 'CUSTOMER' ? '/dashboard' : '/supplier/dashboard'"
            class="flex items-center gap-3 mr-8 group"
          >
            <div class="relative">
              <div class="absolute -inset-1 bg-accent-500/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <img src="../assets/earth.png" alt="Neutral Fuels" class="relative h-9 w-9 rounded-lg" />
            </div>
            <div class="hidden sm:block">
              <span class="text-lg font-bold text-surface-900 dark:text-white tracking-tight">my</span>
              <span class="text-lg font-bold gradient-accent-text tracking-tight">Portal</span>
            </div>
          </router-link>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center gap-1">
            <router-link
              v-if="userProfile.account_type === 'CUSTOMER'"
              to="/dashboard"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path === '/dashboard' }"
            >
              <HomeIcon class="h-4 w-4" />
              <span>Dashboard</span>
            </router-link>

            <router-link
              v-if="userProfile.account_type === 'CUSTOMER'"
              to="/customer-analytics"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path === '/customer-analytics' }"
            >
              <ChartBarIcon class="h-4 w-4" />
              <span>Analytics</span>
            </router-link>

            <router-link
              v-if="userProfile.account_type === 'SUPPLIER'"
              to="/supplier/dashboard"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path === '/supplier/dashboard' }"
            >
              <HomeIcon class="h-4 w-4" />
              <span>Dashboard</span>
            </router-link>

            <router-link
              v-if="userProfile.role === 'ACCOUNT_ADMIN' && userProfile.account_type === 'CUSTOMER'"
              to="/accounts"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path === '/accounts' }"
            >
              <UsersIcon class="h-4 w-4" />
              <span>Users</span>
            </router-link>

            <router-link
              v-if="userProfile.role === 'ACCOUNT_ADMIN' && userProfile.account_type === 'SUPPLIER'"
              to="/supplier/accounts"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path === '/supplier/accounts' }"
            >
              <UsersIcon class="h-4 w-4" />
              <span>Users</span>
            </router-link>

            <router-link
              to="/profile"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path === '/profile' }"
            >
              <UserCircleIcon class="h-4 w-4" />
              <span>Profile</span>
            </router-link>
          </div>
        </div>

        <!-- Right: Controls -->
        <div class="flex items-center gap-2">
          <!-- Theme Toggle -->
          <button
            @click="toggleDarkMode"
            class="relative p-2.5 rounded-xl text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800/60 transition-all duration-200"
            title="Toggle theme"
          >
            <SunIcon v-if="isDark" class="h-5 w-5" />
            <MoonIcon v-else class="h-5 w-5" />
          </button>

          <span
            v-if="userProfile.role === 'PORTAL_SUPER_ADMIN'"
            class="hidden md:inline-flex items-center rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400"
          >
            Internal view
          </span>

          <!-- User Info (Desktop) -->
          <div class="hidden md:flex items-center gap-3 pl-2 pr-1 py-1.5 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800/40 transition-colors cursor-default">
            <div class="h-8 w-8 rounded-lg bg-gradient-to-br from-accent-400 to-cyan-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
              {{ userProfile.first_name?.charAt(0) }}{{ userProfile.last_name?.charAt(0) }}
            </div>
            <div class="text-right hidden lg:block">
              <p class="text-sm font-semibold text-surface-800 dark:text-surface-100 leading-tight">
                {{ userProfile.first_name }} {{ userProfile.last_name }}
              </p>
              <p class="text-xs text-surface-500 dark:text-surface-500 leading-tight">
                {{ userProfile.role?.replaceAll('_', ' ') }}
              </p>
            </div>
          </div>

          <!-- Logout Button (Desktop) -->
          <button
            @click="logout"
            class="hidden md:flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-200"
          >
            <ArrowRightOnRectangleIcon class="h-4 w-4" />
            <span class="hidden lg:inline">Logout</span>
          </button>

          <!-- Mobile menu button -->
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="md:hidden p-2.5 rounded-xl text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800/60 transition-colors"
          >
            <Bars3Icon v-if="!mobileMenuOpen" class="h-5 w-5" />
            <XMarkIcon v-else class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition
      enter-active-class="transition duration-200 ease-spring"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div v-if="mobileMenuOpen" class="md:hidden border-t border-surface-200/50 dark:border-surface-800/50 bg-white/95 dark:bg-surface-950/95 backdrop-blur-xl">
        <div class="px-3 pt-3 pb-4 space-y-1">
          <div v-if="userProfile.role === 'PORTAL_SUPER_ADMIN'" class="px-4 pb-1">
            <span class="inline-flex items-center rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
              Internal view
            </span>
          </div>

          <router-link
            v-if="userProfile.account_type === 'CUSTOMER'"
            to="/dashboard"
            class="mobile-nav-item"
            :class="{ 'mobile-nav-item-active': $route.path === '/dashboard' }"
            @click="mobileMenuOpen = false"
          >
            <HomeIcon class="h-5 w-5" />
            <span>Dashboard</span>
          </router-link>

          <router-link
            v-if="userProfile.account_type === 'CUSTOMER'"
            to="/customer-analytics"
            class="mobile-nav-item"
            :class="{ 'mobile-nav-item-active': $route.path === '/customer-analytics' }"
            @click="mobileMenuOpen = false"
          >
            <ChartBarIcon class="h-5 w-5" />
            <span>Analytics</span>
          </router-link>

          <router-link
            v-if="userProfile.account_type === 'SUPPLIER'"
            to="/supplier/dashboard"
            class="mobile-nav-item"
            :class="{ 'mobile-nav-item-active': $route.path === '/supplier/dashboard' }"
            @click="mobileMenuOpen = false"
          >
            <HomeIcon class="h-5 w-5" />
            <span>Dashboard</span>
          </router-link>

          <router-link
            v-if="userProfile.role === 'ACCOUNT_ADMIN' && userProfile.account_type === 'CUSTOMER'"
            to="/accounts"
            class="mobile-nav-item"
            @click="mobileMenuOpen = false"
          >
            <UsersIcon class="h-5 w-5" />
            <span>Users</span>
          </router-link>

          <router-link
            v-if="userProfile.role === 'ACCOUNT_ADMIN' && userProfile.account_type === 'SUPPLIER'"
            to="/supplier/accounts"
            class="mobile-nav-item"
            @click="mobileMenuOpen = false"
          >
            <UsersIcon class="h-5 w-5" />
            <span>Users</span>
          </router-link>

          <router-link
            to="/profile"
            class="mobile-nav-item"
            @click="mobileMenuOpen = false"
          >
            <UserCircleIcon class="h-5 w-5" />
            <span>Profile</span>
          </router-link>
        </div>

        <!-- Mobile User Info -->
        <div class="px-4 py-4 border-t border-surface-200/50 dark:border-surface-800/50">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="h-9 w-9 rounded-lg bg-gradient-to-br from-accent-400 to-cyan-500 flex items-center justify-center text-white text-sm font-bold">
                {{ userProfile.first_name?.charAt(0) }}{{ userProfile.last_name?.charAt(0) }}
              </div>
              <div>
                <p class="text-sm font-semibold text-surface-800 dark:text-surface-100">
                  {{ userProfile.first_name }} {{ userProfile.last_name }}
                </p>
                <p class="text-xs text-surface-500">
                  {{ userProfile.email }}
                </p>
              </div>
            </div>
            <button
              @click="logout"
              class="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <ArrowRightOnRectangleIcon class="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useDarkMode } from '@/composables/useDarkMode'
import {
  HomeIcon,
  ChartBarIcon,
  UsersIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/vue/24/outline'

const router = useRouter()
const route = useRoute()
const store = useStore()
const { isDark, toggleDarkMode } = useDarkMode()

const mobileMenuOpen = ref(false)
const scrolled = ref(false)
const userProfile = computed(() => store.state.userProfile)

const handleScroll = () => {
  scrolled.value = window.scrollY > 8
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const logout = async () => {
  await store.dispatch('logout')
  router.push({ name: 'Login' })
}
</script>

<style scoped>
.nav-item {
  @apply flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl
         text-surface-500 dark:text-surface-400
         hover:text-surface-800 dark:hover:text-surface-100
         hover:bg-surface-100 dark:hover:bg-surface-800/50
         transition-all duration-200;
}

.nav-item-active {
  @apply text-accent-700 dark:text-accent-400 bg-accent-50 dark:bg-accent-500/10 font-semibold;
}

.mobile-nav-item {
  @apply flex items-center gap-3 px-4 py-3 text-base font-medium rounded-xl
         text-surface-600 dark:text-surface-300
         hover:bg-surface-100 dark:hover:bg-surface-800/60
         transition-all duration-200;
}

.mobile-nav-item-active {
  @apply text-accent-700 dark:text-accent-400 bg-accent-50 dark:bg-accent-500/10;
}
</style>
